import React, { useState, useEffect, useCallback } from "react";
import {
  Tabs,
  Tab,
  Box,
  Menu,
  MenuItem,
  FormControlLabel,
} from "@mui/material";
import ChevronDown from "@mui/icons-material/KeyboardArrowDown";
import "./index.css";
import "../../index.css";
import ProductComponentBox from "../ProductComponentBox";
import unfilledCheckbox from "../../../../assets/design-revamp-product/product-icons/unselected-variation-checklist.svg";
import filledCheckbox from "../../../../assets/design-revamp-product/product-icons/selected-variation-checklist.svg";
import Carousel from "react-multi-carousel-18";
import "react-multi-carousel-18/lib/styles.css";
import customLeftArrow from "../../../../assets/design-revamp-product/product-icons/leftArrow.svg";
import customRightArrow from "../../../../assets/design-revamp-product/product-icons/rightArrow.svg";
import checkCount from "../../../../assets/design-revamp-product/product-icons/count-checks.svg";
import removeCounts from "../../../../assets/design-revamp-product/product-icons/remove-counts.svg";
import pencilIcon from "../../../../assets/shapes_svg/product/edit-icon-subgroup.svg";

const FilterableProductBar = React.memo(
  ({
    products,
    subgroups,
    onProductSelection,
    isProductFront,
    subgroupName,
    idSubgroup,
    setFilteredResults,
    editStates,
    setCustomSubCat,
    handleSaveCustomSubCat,
    handleEditSubgroup,
    trackRecentSubgroup,
    subgroup,
  }) => {
    const [anchorEls, setAnchorEls] = useState({});
    const [selectedSubgroups, setSelectedSubgroups] = useState([]);
    const [selectedVariations, setSelectedVariations] = useState([]);
    const [selectedVariationValues, setSelectedVariationValues] = useState({});
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedTab, setSelectedTab] = useState(0);

    const getSubgroupName = (subgroupId) => {
      const subgroup = subgroups.find((sg) => sg.id === subgroupId);
      return subgroup ? subgroup.name : "";
    };

    useEffect(() => {
      if (idSubgroup && isProductFront) {
        setSelectedSubgroups((currentSubgroups) => {
          // Only update if idSubgroup isn't already included
          if (!currentSubgroups.includes(idSubgroup)) {
            return [idSubgroup];
          }
          return currentSubgroups;
        });
      }
    }, [idSubgroup, isProductFront]);

    const menuStyles = {
      "& .MuiPaper-root": {
        minWidth: "250px",
        maxWidth: "400px",
      },
      "& .MuiMenuItem-root": {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        padding: "8px 16px",
      },
      "& .MuiFormControlLabel-root": {
        margin: 0,
        width: "fit-content",
        justifyContent: "space-between",
      },
      "& .MuiFormControlLabel-label": {
        flex: 1,
        marginRight: "16px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        width: "fit-content",
      },
    };

    const handleTabChange = (event, newValue) => {
      setSelectedTab(newValue);

      // Get the tab info based on the newValue and open the corresponding menu
      let tabId;
      if (newValue === 0) {
        tabId = "subgroups";
      } else if (newValue === selectedVariations.length + 1) {
        tabId = "variations";
      } else {
        tabId = selectedVariations[newValue - 1];
      }

      // Track if needed
      if (isProductFront && trackRecentSubgroup) {
        trackRecentSubgroup(subgroup);
      }

      // Open the menu
      handleClick(event, tabId);
    };

    // Keep the handleClick function for menu opening
    const handleClick = (event, tabId) => {
      // Prevent event from bubbling up to parent handlers
      event.stopPropagation();

      setAnchorEls((prev) => ({
        ...prev,
        [tabId]: event.currentTarget,
      }));
    };

    // Keep the handleClose function for menu closing
    const handleClose = (tabId) => {
      setAnchorEls((prev) => ({
        ...prev,
        [tabId]: null,
      }));
    };

    // Handle product selection
    const handleProductSelect = useCallback(
      (product) => {
        setSelectedProducts((prev) => {
          const newSelection = prev.includes(product.id)
            ? prev.filter((id) => id !== product.id)
            : [...prev, product.id];

          // Notify parent component of selection change
          const selectedProductsData = products.filter((p) =>
            newSelection.includes(p.id)
          );
          onProductSelection(selectedProductsData);

          return newSelection;
        });
      },
      [products, onProductSelection]
    );

    // Get all unique variations across selected subgroups
    const getAvailableVariations = () => {
      if (selectedSubgroups.length === 0) return [];

      const variations = new Set();
      subgroups
        .filter((sg) => selectedSubgroups.includes(sg.id))
        .forEach((sg) => {
          sg.variation_categories.forEach((vc) => {
            variations.add(vc.name);
          });
        });
      return Array.from(variations);
    };

    // Get all values for a specific variation
    const getVariationValues = (variationName) => {
      const values = new Set();
      subgroups
        .filter((sg) => selectedSubgroups.includes(sg.id))
        .forEach((sg) => {
          sg.variation_categories
            .filter((vc) => vc.name === variationName)
            .forEach((vc) => {
              vc.values.forEach((v) => values.add(v.value));
            });
        });
      return Array.from(values);
    };

    // Handle subgroup selection
    const handleSubgroupSelect = (subgroupId) => {
      setSelectedSubgroups((prev) => {
        const newSelection = prev.includes(subgroupId)
          ? prev.filter((id) => id !== subgroupId)
          : [...prev, subgroupId];

        // Reset variation selections if no subgroups are selected
        if (newSelection.length === 0) {
          setSelectedVariations([]);
          setSelectedVariationValues({});
        }

        return newSelection;
      });
    };

    // Handle variation selection
    const handleVariationSelect = (variationName) => {
      setSelectedVariations((prev) => {
        if (prev.includes(variationName)) {
          // Remove variation and its values
          const newVariationValues = { ...selectedVariationValues };
          delete newVariationValues[variationName];
          setSelectedVariationValues(newVariationValues);
          return prev.filter((v) => v !== variationName);
        }
        return [...prev, variationName];
      });
    };

    // Handle variation value selection
    const handleVariationValueSelect = (variation, value) => {
      setSelectedVariationValues((prev) => {
        const currentValues = prev[variation] || [];
        const newValues = currentValues.includes(value)
          ? currentValues.filter((v) => v !== value)
          : [...currentValues, value];

        return {
          ...prev,
          [variation]: newValues,
        };
      });
    };

    // Filter products based on selections
    useEffect(() => {
      let filtered = [];

      // First filter by subgroups
      if (selectedSubgroups.length > 0) {
        filtered = products.filter((product) =>
          selectedSubgroups.includes(product.subgroup)
        );
      } else {
        filtered = products;
      }

      if (selectedVariations.length > 0) {
        selectedVariations.forEach((variation) => {
          filtered = filtered.filter((product) => {
            return product.variation_categories.some(
              (vc) => vc.name === variation
            );
          });
        });
      }

      // Then filter by variation values
      Object.entries(selectedVariationValues).forEach(([variation, values]) => {
        if (values.length > 0) {
          filtered = filtered.filter((product) => {
            return product.variation_categories.some(
              (vc) =>
                vc.name === variation &&
                vc.values.some((v) => values.includes(v.value))
            );
          });
        }
      });

      setFilteredProducts(filtered);
      if (setFilteredResults) {
        setFilteredResults((prevResults) => ({
          ...prevResults,
          [idSubgroup]: filtered,
        }));
      }
    }, [
      selectedSubgroups,
      selectedVariationValues,
      selectedVariations,
      products,
      subgroups,
      setFilteredResults,
      idSubgroup,
    ]);

    const handleUnselectAll = () => {
      setSelectedProducts([]);
    };

    // Updated responsive configuration
    const responsive = {
      superLargeDesktop: {
        breakpoint: { max: 4000, min: 1600 },
        items: 3,
        slidesToSlide: 1,
      },
      desktop: {
        breakpoint: { max: 1600, min: 1024 },
        items: 3,
        slidesToSlide: 1,
      },
      tablet: {
        breakpoint: { max: 1024, min: 768 },
        items: 2,
        slidesToSlide: 1,
      },
      mobile: {
        breakpoint: { max: 768, min: 0 },
        items: 1,
        slidesToSlide: 1,
      },
    };

    // Updated custom arrows with proper event handling
    const CustomLeftArrow = ({ onClick }) => {
      return (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick();
          }}
          style={{
            position: "absolute",
            top: "50%",
            left: "-25px",
            transform: "translateY(-50%)",
            cursor: "pointer",
            background: "none",
            border: "none",
            zIndex: 10,
            padding: "5px",
          }}
          aria-label="Previous slide"
        >
          <img
            src={customLeftArrow}
            alt="Left Arrow Carousel"
            width={50}
            height={50}
            style={{ display: "block" }}
          />
        </button>
      );
    };

    const CustomRightArrow = ({ onClick }) => {
      return (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick();
          }}
          style={{
            position: "absolute",
            top: "50%",
            right: "-25px",
            transform: "translateY(-50%)",
            cursor: "pointer",
            background: "none",
            border: "none",
            zIndex: 10,
            padding: "5px",
          }}
          aria-label="Next slide"
        >
          <img
            src={customRightArrow}
            alt="Right Arrow Carousel"
            width={50}
            height={50}
            style={{ display: "block" }}
          />
        </button>
      );
    };

    const handleTabClick = (event, tabId, tabIndex) => {
      setSelectedTab(tabIndex);
      if (isProductFront && trackRecentSubgroup) {
        trackRecentSubgroup(subgroup);
      }

      // Only open menu if it's not already open
      if (!anchorEls[tabId]) {
        setAnchorEls((prev) => ({
          ...prev,
          [tabId]: event.currentTarget,
        }));
      }
    };

    // Style helper function
    const getColor = (tabIndex) => {
      const cycledIndex = (tabIndex - 1) % 4;
      if (tabIndex === 0) return "rgba(102, 70, 80, 0.9)";

      switch (cycledIndex) {
        case 0:
          return "rgba(102, 70, 80, 0.8)";
        case 1:
          return "rgba(102, 70, 80, 0.6)";
        case 2:
          return "rgba(102, 70, 80, 0.4)";
        case 3:
          return "rgba(102, 70, 80, 0.2)";
        default:
          return "rgba(102, 70, 80, 0.4)";
      }
    };

    return (
      <div
        className="w-full smooth-fade"
        style={{
          position: isProductFront ? "absolute" : "initial",
          top: "-25px",
        }}
      >
        <Box
          sx={{ flexGrow: 1, position: "relative", mb: isProductFront ? 0 : 4 }}
        >
          <Tabs
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            aria-label="Filter Product Bar"
            className="product-bar-container"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              "& .MuiTabs-scrollButtons": {
                color: "#664650",
              },
              "& .Mui-selected": {
                color: "white !important",
              },
              maxWidth: "fit-content",
            }}
            value={selectedTab}
            onChange={handleTabChange}
          >
            {/* Subgroups Tab */}
            <Tab
              className="product-bar-tab"
              label={
                <div className="flex items-center">
                  {isProductFront ? (
                    editStates[idSubgroup] ? (
                      <input
                        type="text"
                        value={subgroupName}
                        onChange={(e) => setCustomSubCat(e.target.value)}
                        onBlur={() =>
                          handleSaveCustomSubCat(subgroupName, idSubgroup)
                        }
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            handleSaveCustomSubCat(subgroupName, idSubgroup);
                          }
                        }}
                        autoFocus
                        style={{
                          background: "transparent",
                          border: "none",
                          outline: "none",
                          color: "white",
                        }}
                      />
                    ) : (
                      <div className="flex items-center">
                        <span>{subgroupName}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            trackRecentSubgroup(subgroup);
                            handleEditSubgroup(idSubgroup, subgroupName);
                          }}
                          style={{
                            border: "none",
                            background: "transparent",
                            marginLeft: "5px",
                          }}
                        >
                          <img
                            src={pencilIcon}
                            width={20}
                            height={20}
                            alt="Edit Subgroup Name"
                          />
                        </button>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center">
                      <span>Subgroups</span>
                      <ChevronDown className="ml-1" size={16} />
                    </div>
                  )}
                </div>
              }
              onClick={(e) =>
                !isProductFront && handleTabClick(e, "subgroups", 0)
              }
              sx={{
                backgroundColor: getColor(0),
                color: "white",
                fontWeight: "bold",
                zIndex: selectedVariations.length + 2,
                textTransform: "capitalize",
              }}
            />

            {/* Selected Variation Value Tabs */}
            {selectedVariations.map((variation, index) => (
              <Tab
                key={variation}
                className="product-bar-tab"
                label={
                  <div className="flex items-center">
                    <span>{variation}</span>
                    <ChevronDown className="ml-1" size={16} />
                  </div>
                }
                onClick={(e) => handleTabClick(e, variation, index + 1)}
                sx={{
                  backgroundColor: getColor(index + 1),
                  marginLeft: "-15px",
                  color: "white",
                  zIndex: selectedVariations.length - index,
                  textTransform: "capitalize",
                }}
              />
            ))}

            {/* Variations Tab */}
            <Tab
              className="product-bar-tab"
              label={
                <div className="flex items-center">
                  <span>Variations</span>
                  <ChevronDown className="ml-1" size={16} />
                </div>
              }
              onClick={(e) =>
                handleTabClick(e, "variations", selectedVariations.length + 1)
              }
              sx={{
                backgroundColor: getColor(selectedVariations.length + 1),
                marginLeft: "-15px",
                color: "white",
                zIndex: 1,
                textTransform: "capitalize",
              }}
            />
          </Tabs>

          {/* Subgroups Menu */}
          {!isProductFront && (
            <Menu
              anchorEl={anchorEls["subgroups"]}
              open={Boolean(anchorEls["subgroups"])}
              onClose={() => handleClose("subgroups")}
              PaperProps={{ className: "dropdown-field-variants" }}
              sx={menuStyles}
            >
              {subgroups.map((subgroup) => (
                <MenuItem
                  key={subgroup.id}
                  onClick={() => handleSubgroupSelect(subgroup.id)}
                >
                  <FormControlLabel
                    control={
                      <img
                        src={
                          selectedSubgroups.includes(subgroup.id)
                            ? filledCheckbox
                            : unfilledCheckbox
                        }
                        alt="Checkbox"
                        style={{ width: 16, height: 16 }}
                      />
                    }
                    label={subgroup.name}
                    labelPlacement="start"
                    sx={{
                      color: "#664650",
                      "& .MuiFormControlLabel-label": {
                        maxWidth: "fit-content",
                      },
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>
          )}

          {/* Variations Menu */}
          <Menu
            anchorEl={anchorEls["variations"]}
            open={Boolean(anchorEls["variations"])}
            onClose={() => handleClose("variations")}
            PaperProps={{ className: "dropdown-field-variants" }}
            sx={menuStyles}
          >
            {getAvailableVariations().map((variation) => (
              <MenuItem
                key={variation}
                onClick={() => handleVariationSelect(variation)}
              >
                <FormControlLabel
                  control={
                    <img
                      src={
                        selectedVariations.includes(variation)
                          ? filledCheckbox
                          : unfilledCheckbox
                      }
                      alt="Checkbox"
                      style={{ width: 16, height: 16 }}
                    />
                  }
                  label={variation}
                  labelPlacement="start"
                  sx={{
                    color: "#664650",
                    "& .MuiFormControlLabel-label": {
                      maxWidth: "fit-content",
                    },
                  }}
                />
              </MenuItem>
            ))}
          </Menu>

          {/* Variation Values Menus */}
          {selectedVariations.map((variation) => (
            <Menu
              key={variation}
              anchorEl={anchorEls[variation]}
              open={Boolean(anchorEls[variation])}
              onClose={() => handleClose(variation)}
              PaperProps={{ className: "dropdown-field-variants" }}
              sx={menuStyles}
            >
              {getVariationValues(variation).map((value) => (
                <MenuItem
                  key={value}
                  onClick={() => handleVariationValueSelect(variation, value)}
                >
                  <FormControlLabel
                    control={
                      <img
                        src={
                          selectedVariationValues[variation]?.includes(value)
                            ? filledCheckbox
                            : unfilledCheckbox
                        }
                        alt="Checkbox"
                        style={{ width: 16, height: 16 }}
                      />
                    }
                    label={value}
                    labelPlacement="start"
                    sx={{
                      color: "#664650",
                      "& .MuiFormControlLabel-label": {
                        maxWidth: "fit-content",
                      },
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>
          ))}
        </Box>

        {/* Filtered Products Display */}
        {!isProductFront && (
          <div
            className="mt-8 smooth-fade carousel-products-bg"
            style={{
              display: "flex",
              justifyContent: "center",
              position: "relative",
              minHeight: "200px", // Add minimum height for carousel
            }}
          >
            {selectedProducts && selectedProducts.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "-45px",
                  right: "25px",
                  cursor: "pointer",
                  zIndex: 5,
                }}
                onClick={handleUnselectAll}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "50px",
                    color: "white",
                  }}
                  className="count-number-bg"
                >
                  <img
                    src={removeCounts}
                    alt="Unselect Products Checked"
                    width={40}
                    height={40}
                    style={{ marginLeft: "-18px", marginRight: "5px" }}
                  />
                  {selectedProducts.length}
                </div>
              </div>
            )}
            <div
              style={{
                position: "absolute",
                top: "-5px",
                right: "25px",
                zIndex: 5,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "50px",
                  color: "white",
                }}
                className="count-number-bg"
              >
                <img
                  src={checkCount}
                  alt="Numbers of Products Checked"
                  width={40}
                  height={40}
                  style={{ marginLeft: "-18px", marginRight: "5px" }}
                />
                {selectedProducts.length}
              </div>
            </div>

            {/* Only render carousel if there are products */}
            {filteredProducts.length > 0 ? (
              filteredProducts.length > 4 ? (
                <div style={{ width: "100%", position: "relative" }}>
                  <Carousel
                    responsive={responsive}
                    infinite={false}
                    keyBoardControl={true}
                    customTransition="transform 300ms ease-in-out"
                    transitionDuration={300}
                    containerClass="carousel-container"
                    itemClass="carousel-item"
                    customLeftArrow={<CustomLeftArrow />}
                    customRightArrow={<CustomRightArrow />}
                    arrows={true}
                    swipeable={true}
                    draggable={true}
                    showDots={false}
                    ssr={false}
                    autoPlay={false}
                    focusOnSelect={false}
                    centerMode={false}
                    partialVisible={false}
                  >
                    {filteredProducts.map((product, index) => (
                      <div
                        key={`${product.id}-${index}`}
                        style={{
                          padding: "0 10px",
                          height: "auto",
                        }}
                      >
                        <ProductComponentBox
                          name={product?.name}
                          prodNum={product?.product_num}
                          variationValues={product?.variation_categories}
                          isAR={true}
                          subgroup={getSubgroupName(product.subgroup)}
                          productId={product.id}
                          isSelected={selectedProducts.includes(product.id)}
                          onSelect={() => handleProductSelect(product)}
                        />
                      </div>
                    ))}
                  </Carousel>
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: "20px",
                    padding: "20px",
                  }}
                >
                  {filteredProducts.map((product, index) => (
                    <div
                      key={`${product.id}-${index}`}
                      style={{
                        flex: "0 0 auto",
                        maxWidth: "300px",
                      }}
                    >
                      <ProductComponentBox
                        name={product?.name}
                        prodNum={product?.product_num}
                        variationValues={product?.variation_categories}
                        isAR={true}
                        subgroup={getSubgroupName(product.subgroup)}
                        productId={product.id}
                        isSelected={selectedProducts.includes(product.id)}
                        onSelect={() => handleProductSelect(product)}
                      />
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  color: "#664650",
                  fontSize: "18px",
                }}
              >
                No products found matching the current filters.
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

export default FilterableProductBar;
